import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { amplitudeExperiment } from 'freedom-shared';

import {
  watchlistApi,
  useWatchlist,
  mapWatchlistOfferRecommendation,
} from '@/features/favorites';
import { hotOffersModel } from '@/features/hot-offers';

import { useUserStore } from '@/shared/model';

import { recommendationsApi } from '../api';

const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Treatment variant key for the `avfree_recommended_offers` experiment.
 * The block is shown only when the user is in this variant.
 */
const RECOMMENDED_OFFERS_TREATMENT_VARIANT = 'b';

/**
 * Drives the "Подобрано для вас" block:
 *
 *   1. Fetches a list of recommended destinations from the (currently
 *      mocked) `/recommendations` endpoint.
 *   2. Feeds those `direction` strings into `/watchlist/offers/by-direction`
 *      to get the cheapest offers per direction.
 *
 * Both queries are gated by the `avfree_recommended_offers` A/B
 * experiment, so users in the control group never trigger either request.
 */
export function useRecommendedOffers() {
  const watchlist = useWatchlist();
  const userStore = useUserStore();
  const { user } = storeToRefs(userStore);

  /**
   * The recommended-offers block reuses the hot-offers departure city so
   * both blocks stay consistent when the user switches their origin city.
   * `origCityCode` is reactive and falls back to `defaultDepartureCityCode`
   * when the user has not picked one yet.
   */
  const hotOffersStore = hotOffersModel.useHotOffersStore();
  const { origCityCode } = storeToRefs(hotOffersStore);

  const { isVariant: isExperimentEnabled, isExperimentLoading } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.RECOMMENDED_OFFERS,
      RECOMMENDED_OFFERS_TREATMENT_VARIANT,
    );

  const isExperimentResolved = computed(
    () => !isExperimentLoading.value && isExperimentEnabled.value,
  );

  const userPhone = computed(() => user.value?.username ?? '');

  const recommendationsQuery = useQuery({
    queryKey: ['recommendations', userPhone],
    queryFn: () => recommendationsApi.getRecommendations(userPhone.value),
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: 3,
    enabled: computed(
      () => isExperimentResolved.value && Boolean(userPhone.value),
    ),
  });

  /**
   * Sorted list of recommended destinations as plain `direction` strings,
   * ready to be sent as repeated `direction=...` query params.
   */
  const directions = computed(() => {
    const items = recommendationsQuery.data.value?.recommendations ?? [];
    return [...items]
      .sort((a, b) => a.rank - b.rank)
      .map(item => item.direction);
  });

  const isOffersQueryEnabled = computed(
    () =>
      isExperimentResolved.value &&
      directions.value.length > 0 &&
      Boolean(origCityCode.value),
  );

  const recommendedQuery = useQuery({
    queryKey: ['recommended-offers', origCityCode, directions],
    queryFn: () =>
      watchlistApi.getWatchlistOffersByDirection(
        origCityCode.value,
        directions.value,
      ),
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: 3,
    enabled: isOffersQueryEnabled,
  });

  const offers = computed(() => {
    const data = recommendedQuery.data.value;
    const items = data?.items ?? [];
    /**
     * Prefer the dict bundled with this response (it's scoped to exactly
     * the cities / airlines we got back) and fall back to the user's
     * watchlist dict so unauthenticated / empty-watchlist users still
     * see localized names if the backend ever omits the dict.
     */
    const dict = data?.dict ?? watchlist.watchlistDict.value;
    return items.map(item => mapWatchlistOfferRecommendation(item, dict));
  });

  const isLoading = computed(() => {
    if (!isExperimentResolved.value) {
      return false;
    }
    return (
      recommendationsQuery.isLoading.value ||
      (isOffersQueryEnabled.value && recommendedQuery.isLoading.value)
    );
  });
  const hasOffers = computed(() => offers.value.length > 0);

  return {
    offers,
    isLoading,
    hasOffers,
    /**
     * `true` when the experiment has resolved and the user is in the
     * treatment variant — UI consumers should use this to gate render.
     */
    isExperimentEnabled,
    isExperimentLoading,
  };
}
